import { Accordion } from "./Accordion";
import { FiltersCheckBox } from "./FiltersCheckBox";
import { clientConfig } from "../../../config";
import { MainTracker } from "../../../PixelTrackers/MainTracker";
import { useCustomer } from "../../../customHooks/useCustomer";

const productTypeIcons = {
    flower: `${clientConfig.IMAGES_URL}uploads/choose_treatment/flowerFilterIcon.png`,
    extract: `${clientConfig.IMAGES_URL}uploads/choose_treatment/extractFilterIcon.png`,
    edible: `${clientConfig.IMAGES_URL}uploads/choose_treatment/edibleFilterIcon.png`,
    vape: `${clientConfig.IMAGES_URL}uploads/choose_treatment/vapeFilterIcon.png`,
}

type LayoutVersion = "filtersMenu" | "chooseTreatmentHeader";

type Props = {
    filterOptions: { value: string, isSelected: boolean, name: string, keyWordTitle: string }[];
    handleChooseFilter: (item: { value: string, isSelected: boolean }, isMultiSelect?: boolean) => () => void;
    layoutVersion?: LayoutVersion;
}

export const CannabisProductTypeFilter: React.FC<Props> = ({ 
    filterOptions, 
    handleChooseFilter,
    layoutVersion = "filtersMenu"
}) => {
    const { customerState } = useCustomer();
    const productTypeOrder = ['flower', 'vape', 'edible', 'extract' ];

    // Map product type values to event names
    const productTypeEventNames: Record<string, string> = {
        flower: "Flowers-Filter",
        extract: "Extracts-Filter",
        edible: "Edibles-Filter",
        vape: "Vape-Filter",
    };

    const filterOptionsToExclude: string[] = []; // add here the product types to exclude from the filter (e.g. ["flower", "extract", "edible", "vape"]), used to contain the "vape" product type.

    // Sort filterOptions based on productTypeOrder
    const sortedOptions = [...filterOptions].sort((a, b) => {
        return productTypeOrder.indexOf(a.value) - productTypeOrder.indexOf(b.value);
    });

    // Don't render the product-type filter when there's only one (or zero) option — a single
    // option filters nothing (e.g. catalog 3941 has flowers only). Hides both the inline header
    // bar and the side-menu chip list.
    const visibleOptions = sortedOptions.filter(item => !filterOptionsToExclude.includes(item.value));
    if (visibleOptions.length <= 1) return null;

    // Wrapper to add tracking for type filter
    const handleFilterWithTracking = (item: { value: string, isSelected: boolean }, isMultiSelect?: boolean) => () => {
        const wasSelected = item.isSelected;
        
        // Track filter_selected event when a filter is selected (critical for tracking product discovery behavior)
        // Only track when selecting (not deselecting)
        if (!wasSelected) {
            const userId = customerState?.customerID || null;
            
            MainTracker.track("custom", "filter_selected", {
                user_id: userId,
                filter_type: "product_type",
                filter_value: item.value
            });
        }
        
        // Keep legacy tracking for backward compatibility
        const eventName = productTypeEventNames[item.value] || `${item.value}-Filter`;
        const action = item.isSelected ? "remove" : "add";
        MainTracker.track("custom", eventName, { [action]: true });
        
        handleChooseFilter(item, isMultiSelect)();
    };

    // Common logic for both layouts
    const selectedItems = sortedOptions.filter(item => item.isSelected);

    const handleClear = () => {
        MainTracker.track("custom", "All-Products-Filter", { Add: true });
        
        selectedItems.forEach(item => {
            handleChooseFilter(item, true)();
        });
    };

    // Common icon component
    const ProductIcon = ({ value }: { value: string }) => (
        <div
            className={`
                h-[${layoutVersion === "filtersMenu" ? "50" : "40"}px]
                w-[${layoutVersion === "filtersMenu" ? "50" : "40"}px]
                md:h-[50px]
                md:w-[50px]
                bg-[#F9F7FF]
                relative
                overflow-visible
            `}
            style={{ borderRadius: "5.6px 0px 14px 5.6px" }}
        >
            <img
                id="product-type__icon"
                className={`h-[${layoutVersion === "filtersMenu" ? "50" : "40"}px] md:h-[50px]`}
                src={productTypeIcons[value as keyof typeof productTypeIcons]}
                alt=""
                style={{
                    position: "absolute",
                    left: "50%",
                    top: "0%",
                    transform: `translate(-50%, -15%)`
                }}
            />
        </div>
    );

    // compoent for Filters Menu that opens to the side
    if (layoutVersion === "filtersMenu") {
        return (
            <Accordion title={filterOptions[0].keyWordTitle}>
                <div
                    id="choose-treatment-product-type-filter"
                    className="flex flex-col gap-3 py-2 md:py-4"
                >
                    {sortedOptions.map(item => (
                        <div
                            key={item.value}
                            id={`filter_${item.value}`}
                            className="
                                cursor-pointer
                                flex
                                items-center
                                gap-3 
                                pr-3
                                transition-all
                                duration-300
                            "
                            onClick={handleFilterWithTracking(item, true)} 
                            style={{
                                border: "1px solid #DBE1EB",
                                borderRadius: "6px",
                            }}
                        >
                            {productTypeIcons[item.value as keyof typeof productTypeIcons] && (
                                <ProductIcon value={item.value} />
                            )}
                            
                            <span
                                id="product-type__title"
                                className="text-[14px] md:text-[16px] flex-1 first-letter:capitalize"
                            >
                                {item.name}
                            </span>

                            <FiltersCheckBox isSelected={item.isSelected} />
                        </div>
                    ))}
                </div>
            </Accordion>
        );
    }

    // Choose Treatment Header Layout in the main display of the page under the search bar
    return (
        <div className="w-full min-w-0 mt-3 select-none">
            <div className="flex flex-row justify-between items-center mb-3">
                <span className="text-[14px] text-[#363636] font-roboto">
                    Produkttyp
                </span>
                
                <button
                    className="text-[12px] text-[#293735] transition-colors border-none bg-transparent underline flex-shrink-0 cursor-pointer"
                    onClick={handleClear}
                >
                    Löschen
                </button>
            </div>

            {/* Filters Row - Desktop has Clear button at the end */}
            <div className="flex gap-2 md:gap-4 w-full overflow-x-auto md:overflow-x-hidden pt-2 -mt-2" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                {visibleOptions.map(item => (
                    <div
                        key={item.value}
                        id={`inline-filter_${item.value}`}
                        className={`
                            cursor-pointer
                            flex
                            shrink-0 md:shrink
                            flex-1
                            min-w-[100px]
                            items-center
                            justify-between
                            gap-2 md:gap-5
                            pr-2 md:pr-4
                            transition-all
                            duration-500
                            bg-white
                        `}
                        onClick={handleFilterWithTracking(item, true)}
                        style={{
                            border: item.isSelected ? "2px solid #269C6E" : "1px solid #DBE1EB",
                            borderRadius: "8px",
                        }}
                    >
                        {productTypeIcons[item.value as keyof typeof productTypeIcons] && (
                            <ProductIcon value={item.value} />
                        )}
                        
                        <span
                            className={`
                                text-[14px] md:text-[15px] 
                                text-center 
                                font-medium
                                first-letter:capitalize
                                ${item.isSelected ? 'text-[#269C6E]' : 'text-[#363636]'}
                            `}
                        >
                            {item.name}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
